import React, { useCallback, useMemo, useState } from 'react';

import {
  SidePage,
  Button,
  Stack,
  Grid,
  SearchInput,
  Divider,
  IconButton,
  MetricsPicker,
  ExpandLessIcon,
  ExpandMoreIcon,
  Tooltip,
  HelpOutlineIcon,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { useUpdateDependent } from '@gogame/screens';
import SmartTooltip from 'scenes/Trackers/components/SmartTooltip';
import ApplyButton from 'scenes/Trackers/Tracker/components/ApplyButton';
import {
  MY_CUSTOM_METRIC,
  PUBLIC_CUSTOM_METRIC,
} from 'scenes/Trackers/Tracker/constants';
import { useLoadFavouriteMetrics } from 'scenes/Trackers/Tracker/data';
import { useDeleteMetric } from 'scenes/Trackers/Tracker/data/mutations';
import { useLoadFavouriteMetricFilters } from 'scenes/Trackers/Tracker/data/useLoadFavouriteMetricFilters';

import {
  MetricFormCreate,
  MetricFormEdit,
} from '../TrackerDataTable/components/MetricForm';
import ActionsComponent from './Actions';
import FavouriteMetrics from './FavouriteMetrics';
import EventNumber from './FavouriteMetrics/EventNumber';
import GroupAction from './GroupActions';
import { useGAMetricSearch } from './_gaMetricSearch';
import { formatDataWithCustomMetricsGroup } from './formatMetricsIntoGroups';
import { getTokens, names } from './utils';

const DEFAULT_SELECTED = [];
const DEFAULT_LIST = [];
const GROUP_ADVANCED_DEBUG = 17;

export const customMetricGroupName = 'My Custom Metrics';

const getTitleName = ({ groupId, groupName }) => {
  if (groupId === MY_CUSTOM_METRIC) {
    return 'Custom';
  } else if (groupId === PUBLIC_CUSTOM_METRIC) {
    return 'Public Custom';
  }
  return groupName;
};

const getMetricValue = (v) => v?.name;
const ColumnsPicker = ({
  value = DEFAULT_SELECTED,
  metrics = DEFAULT_LIST,
  onClose,
  onSubmit,
  loading,
  fetching,
  open,
  onExited,
}) => {
  const [selected, setSelected] = useState(value);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const updateDependent = useUpdateDependent();
  const [deleteCustomMetric] = useDeleteMetric();
  const [childComponent, showChildComponent] = useChildComponent();

  const handleCreateMetric = async ({ groupName, groupId }) => {
    showChildComponent(MetricFormCreate, {
      title: `Add ${getTitleName({ groupName, groupId })} Metric`,
      initialValues: {
        groupId,
        isPublic: groupId === PUBLIC_CUSTOM_METRIC,
        type: 'number',
      },
      onSearch: (name) => {
        setSearchQuery(name);
      },
    });
  };

  const handleEditMetric = async ({ id, groupId, groupName }) => {
    showChildComponent(MetricFormEdit, {
      id,
      title: `Edit ${getTitleName({ groupName, groupId })} Metric`,
    });
  };

  const handleDeleteMetric = async (metric) => {
    await deleteCustomMetric({ ...metric, groupName: customMetricGroupName });
    updateDependent();
  };

  const {
    data: favouriteMetrics,
    isLoading,
    isFetching,
  } = useLoadFavouriteMetrics();

  const queryTokens = useMemo(() => {
    if (searchQuery && favouriteMetrics?.length) {
      return getTokens(searchQuery, {
        favouriteMetrics: names(favouriteMetrics),
      });
    }
    return [];
  }, [searchQuery, favouriteMetrics]);

  const favouriteMetricName = useMemo(() => {
    return favouriteMetrics?.length
      ? names(favouriteMetrics).find((mn) => queryTokens.includes(mn)) || ''
      : '';
  }, [queryTokens, favouriteMetrics]);

  const {
    data: favouriteMetricFilters,
    isLoading: isFiltersLoading,
    isFetching: isFiltersFetching,
  } = useLoadFavouriteMetricFilters(favouriteMetricName);

  const eventDropDownValue = useMemo(() => {
    if (favouriteMetricName === 'Game Event') {
      const metricFilter = favouriteMetricFilters?.[0]?.elements.find((el) =>
        queryTokens.includes(el.name)
      );
      return metricFilter?.id;
    }
  }, [queryTokens, favouriteMetricFilters, favouriteMetricName]);

  const handleClearAll = () => {
    setSelected([]);
  };

  const handleSubmit = (apply) => () => {
    const allValues = metrics.map(getMetricValue).filter((v) => !!v);
    const result = selected.filter((metric) => allValues.includes(metric));
    onSubmit({ order: result, apply });
  };

  const handleChipClick = useCallback(
    (name) => {
      if (queryTokens.includes(name)) {
        if (favouriteMetrics.find((metric) => metric.name === name)) {
          setIsFiltersOpen(false);
          setSearchQuery('');
        } else if (
          favouriteMetricFilters.find((filter) =>
            filter.elements.find((el) => el.name === name)
          )
        ) {
          const newSearch = queryTokens.filter((el) => el !== name).join(' ');
          setSearchQuery(newSearch);
        }
      } else {
        if (favouriteMetrics.find((metric) => metric.name === name)) {
          setSearchQuery(name);
          setIsFiltersOpen(true);
        } else {
          setSearchQuery(`${searchQuery} ${name}`);
        }
      }
    },
    [searchQuery, queryTokens, favouriteMetrics]
  );

  useGAMetricSearch({
    searchQuery,
    favouriteMetrics,
    favouriteMetricFilters,
  });

  const dirty = useMemo(() => {
    return selected.toString() !== value.toString();
  }, [selected]);

  React.useEffect(() => {
    if (metrics?.length) {
      const allValues = metrics.map(getMetricValue).filter((v) => !!v);

      const existedSelection = selected.filter((metric) =>
        allValues.includes(metric)
      );

      if (existedSelection.length !== selected.length) {
        setSelected(existedSelection);
      }
    }
  }, [selected, metrics]);

  return (
    <SidePage
      size={'lg'}
      onClose={onClose}
      onExited={onExited}
      loading={loading || isLoading}
      fetching={fetching || isFetching || isFiltersFetching || isFiltersLoading}
      open={open}
    >
      <SidePage.Title title={'Customize Columns'} />
      <SidePage.NewBody disableGutters={true} dense={true} scroll={false}>
        <div style={{ padding: 16 }}>
          <SearchInput
            value={searchQuery}
            onChange={(_e, value) => {
              if (!value) {
                setIsFiltersOpen(false);
              }
              setSearchQuery(value);
            }}
            onClear={() => setIsFiltersOpen(false)}
          />
          <Stack direction={'row'} flexWrap={'wrap'}>
            {favouriteMetrics ? (
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'end'}
              >
                <FavouriteMetrics
                  favouriteMetrics={favouriteMetrics}
                  onChipClick={handleChipClick}
                  queryTokens={queryTokens}
                  name={'Metrics'}
                />
                <IconButton
                  onClick={() => {
                    setIsFiltersOpen((prev) => !prev);
                  }}
                  tooltip={isFiltersOpen ? 'Collapse' : 'Expand'}
                  icon={isFiltersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                />
              </Stack>
            ) : null}
            {isFiltersOpen && !isFiltersLoading && favouriteMetricFilters ? (
              favouriteMetricName === 'Game Event' ? (
                <EventNumber
                  name={favouriteMetricFilters?.[0]?.name}
                  selected={searchQuery?.includes(eventDropDownValue)}
                  elements={favouriteMetricFilters?.[0]?.elements}
                  onChange={setSearchQuery}
                  favouriteMetricName={favouriteMetricName}
                  value={eventDropDownValue}
                />
              ) : (
                favouriteMetricFilters.map(({ elements, name, id }) => (
                  <FavouriteMetrics
                    key={id}
                    favouriteMetrics={elements}
                    onChipClick={handleChipClick}
                    queryTokens={queryTokens}
                    name={name}
                  />
                ))
              )
            ) : null}
          </Stack>
        </div>
        <Divider />
        <MetricsPicker
          grouped={true}
          searchQuery={searchQuery}
          selected={selected}
          metrics={metrics}
          onClearAll={handleClearAll}
          setSelected={setSelected}
          onEditMetric={handleEditMetric}
          formatData={formatDataWithCustomMetricsGroup}
          isInitialExpanded={(row) => {
            return searchQuery && row.id !== GROUP_ADVANCED_DEBUG; // it is not Advanced Debug
          }}
          selectedSecondaryActionHelpRenderer={({ option }) => {
            return (
              <SmartTooltip
                anchor={
                  <HelpOutlineIcon sx={{ fontSize: 16 }} color={'action'} />
                }
                name={option.fullDisplayName || option.displayName}
                formula={option.displayFormula}
                hint={option.hint}
                showGlossary={option.showGlossary}
                glossaryItems={option.glossaryItems}
              />
            );
          }}
          groupAction={(option) => (
            <GroupAction {...option} onAddMetric={handleCreateMetric} />
          )}
          secondaryAction={(option) => (
            <ActionsComponent
              {...option}
              onDeleteMetric={handleDeleteMetric}
              onUpdateMetric={handleEditMetric}
            />
          )}
        />
      </SidePage.NewBody>
      <SidePage.Actions>
        <Grid container justifyContent={'space-between'}>
          <SidePage.CloseButton />
          <Stack spacing={1} direction={'row'}>
            <Tooltip title={'Save filters'} tooltipPlacement={'bottom'}>
              <div>
                <Button onClick={handleSubmit(false)} disabled={!dirty}>
                  Save
                </Button>
              </div>
            </Tooltip>
            <ApplyButton onClick={handleSubmit(true)} dirty={dirty} />
          </Stack>
        </Grid>
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default ColumnsPicker;
